/**
 * 어드민 ROLE 가드·표시.
 * JWT `/admin/auth/me` roles 기준으로 로그인 유지 여부를 판별하고,
 * 메뉴 노출은 permissions(`authPermissions`)로 제어한다.
 *
 * full-access: ROLE_MASTER / ROLE_MANAGER
 * — 메뉴 권한 row 없이 전 메뉴 WRITE.
 * 구 ROLE_ADMIN은 ROLE_MANAGER로 정규화한다.
 */

import type { BadgeProps } from '@/shared/components/ui/badge';

/**
 * 어드민 UI 로그인 허용 역할.
 * 세부 메뉴·API는 full-access role 또는 `/admin/auth/me` permissions로 제어한다.
 */
export const ADMIN_ACCESS_ROLES = [
  'ROLE_MASTER',
  'ROLE_MANAGER',
  'ROLE_GENERAL',
] as const;

/**
 * 백엔드 full-access role — 메뉴 권한 row 없이 전 메뉴 WRITE.
 */
export const FULL_ACCESS_ROLES = ['ROLE_MASTER', 'ROLE_MANAGER'] as const;

/** API role code → UI 표시 라벨 */
export const ROLE_DISPLAY_LABELS = {
  ROLE_MASTER: '마스터',
  ROLE_MANAGER: '관리',
  ROLE_GENERAL: '일반',
} as const;

export type AuthRoleCode = keyof typeof ROLE_DISPLAY_LABELS;

/** 표시 라벨·별칭 → API role code (persist·레거시 enum 정규화용) */
const ROLE_LABEL_TO_CODE: Record<string, AuthRoleCode> = {
  마스터: 'ROLE_MASTER',
  관리: 'ROLE_MANAGER',
  // 구 UI 라벨 호환
  매니저: 'ROLE_MANAGER',
  일반: 'ROLE_GENERAL',
  // API create enum 호환 (ROLE_ 없는 GENERAL)
  GENERAL: 'ROLE_GENERAL',
  // 구 persist 값 호환
  어드민: 'ROLE_MASTER',
  // 레거시 단일 관리자 enum
  ROLE_ADMIN: 'ROLE_MANAGER',
};

/**
 * persist에 남아 있을 수 있는 표시용 라벨을 API role code로 정규화.
 */
export const normalizeAuthRole = (role?: string | null): string => {
  if (!role) return '';
  if (role in ROLE_DISPLAY_LABELS) return role;
  return ROLE_LABEL_TO_CODE[role] ?? role;
};

export const getRoleDisplayLabel = (role?: string | null): string => {
  const normalized = normalizeAuthRole(role);
  if (!normalized) return '사용자';
  return ROLE_DISPLAY_LABELS[normalized as AuthRoleCode] ?? normalized;
};

/** 헤더 등에서 롤을 상태 태그로 구분할 때 사용 */
export const getRoleBadgeVariant = (
  role?: string | null,
): NonNullable<BadgeProps['variant']> => {
  const normalized = normalizeAuthRole(role);
  switch (normalized) {
    case 'ROLE_MASTER':
      return 'default';
    case 'ROLE_MANAGER':
      return 'info';
    case 'ROLE_GENERAL':
      return 'secondary';
    default:
      return 'outline';
  }
};

export const isMaster = (userRole?: string | null): boolean =>
  normalizeAuthRole(userRole) === 'ROLE_MASTER';

/** 마스터·관리 — 사이드바 전 메뉴 노출 (permissions 무시) */
export const isFullAccessRole = (userRole?: string | null): boolean =>
  (FULL_ACCESS_ROLES as readonly string[]).includes(
    normalizeAuthRole(userRole),
  );

/** roles 배열 중 하나라도 full-access면 true */
export const hasFullAccessFromRoles = (
  roles?: string[] | null,
  fallbackRole?: string | null,
): boolean => {
  if (roles?.some((r) => isFullAccessRole(r))) return true;
  return isFullAccessRole(fallbackRole);
};

export const hasRoleAccess = (
  userRole: string | null | undefined,
  allowedRoles: readonly string[] | string[],
): boolean => {
  if (!allowedRoles?.length) {
    return true;
  }
  const normalized = normalizeAuthRole(userRole);
  if (!normalized) {
    return false;
  }
  return allowedRoles.includes(normalized);
};

export const hasAdminAccess = (
  user: { role?: string | null } | null | undefined,
) => hasRoleAccess(user?.role, ADMIN_ACCESS_ROLES);

/**
 * 설정 모드 하위 「계정 관리·감사로그·메뉴 관리」 — full-access만.
 */
export const canOpenAccountManagement = (userRole?: string | null): boolean =>
  isFullAccessRole(userRole);

/** 헤더 「내 프로필」 — 어드민 로그인 역할 전원 */
export const canOpenMyProfile = (userRole?: string | null): boolean =>
  hasRoleAccess(userRole, ADMIN_ACCESS_ROLES);
